/* eslint-disable no-console */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format2Backend } from "../../actions/Layers/util";
import { tenantApiPath } from "../../actions/tenantApi";
import { callAvosApi } from "../../utils/useAvosApiHook";

/**
 * Mutation hook for creating a new layer.
 *
 * Usage:
 *   const mutation = useCreateLayerMutation();
 *   mutation.mutate({tenantId, data: {...}});
 */
export function useCreateLayerMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { tenantId: string; data: any }) => {
            const { tenantId, data } = args;

            if (!tenantId) {
                throw new Error("Cannot create layer without tenantId");
            }

            const url = `${tenantApiPath(tenantId, "layers/")}`;

            const response = await callAvosApi(`/${url}`, { method: "POST", data: format2Backend(data) });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate layers list
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.tenantId],
            });
        },
        onError: (error) => {
            console.error("Failed to create layer:", error);
        },
    });
}

/**
 * Mutation hook for deleting a layer.
 *
 * Usage:
 *   const mutation = useDeleteLayerMutation();
 *   mutation.mutate({layer});
 */
export function useDeleteLayerMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { layer: any }) => {
            const { layer } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error("Cannot delete layer without tenant_id and id");
            }

            const url = `${tenantApiPath(layer.tenant_id, "layers/")}${layer.id}`;

            const response = await callAvosApi(`/${url}`, { method: "DELETE" });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate layers list
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.layer.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to delete layer:", error);
        },
    });
}

export function useTriggerLayerBusinessRulesMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { layer: any }) => {
            const { layer } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error("Cannot trigger layer business rules without tenant_id and id");
            }

            const url = `${tenantApiPath(layer.tenant_id, "layers/")}${layer.id}/business-rules`;
            const response = await callAvosApi(`/${url}`, { method: "GET" });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.layer.tenant_id],
            });
        },
    });
}

/**
 * Mutation hook for adding a child layer.
 *
 * Usage:
 *   const mutation = useAddLayerChildMutation();
 *   mutation.mutate({layer, childLayerId});
 */
export function useAddLayerChildMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { layer: any; childLayerId: string }) => {
            const { layer, childLayerId } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error("Cannot add child to layer without tenant_id and id");
            }

            const url = `${tenantApiPath(layer.tenant_id, "layers/")}${layer.id}/children`;

            const response = await callAvosApi(`/${url}`, { method: "POST",
                data: {
                    parent_layer_id: layer.id,
                    layer_id: childLayerId,
                } });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate layer hierarchy
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.layer.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to add layer child:", error);
        },
    });
}

/**
 * Mutation hook for adding a parent layer.
 *
 * Usage:
 *   const mutation = useAddLayerParentMutation();
 *   mutation.mutate({layer, parentLayerId});
 */
export function useAddLayerParentMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { layer: any; parentLayerId: string }) => {
            const { layer, parentLayerId } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error("Cannot add parent to layer without tenant_id and id");
            }

            const url = `${tenantApiPath(layer.tenant_id, "layers/")}${layer.id}/parents`;

            const response = await callAvosApi(`/${url}`, { method: "POST",
                data: {
                    parent_layer_id: parentLayerId,
                    layer_id: layer.id,
                } });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate layer hierarchy
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.layer.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to add layer parent:", error);
        },
    });
}

/**
 * Mutation hook for removing a parent layer.
 *
 * Usage:
 *   const mutation = useRemoveLayerParentMutation();
 *   mutation.mutate({layer, parentLayerId});
 */
export function useRemoveLayerParentMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { layer: any; parentLayerId: string }) => {
            const { layer, parentLayerId } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error(
                    "Cannot remove parent from layer without tenant_id and id"
                );
            }

            const url = `${tenantApiPath(layer.tenant_id, "layers/")}${layer.id}/parents/${parentLayerId}`;

            const response = await callAvosApi(`/${url}`, { method: "DELETE" });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate layer hierarchy
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.layer.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to remove layer parent:", error);
        },
    });
}

/**
 * Mutation hook for adding layer allocation (ripening room assignment).
 *
 * Usage:
 *   const mutation = useAddLayerAllocationMutation();
 *   mutation.mutate({tenantId, layer, stage, location});
 */
export function useAddLayerAllocationMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: {
      tenantId: string;
      layerId: string;
      stage: string;
      locationId?: string;
      includeChildren?: boolean;
      facility?: string;
    }) => {
            const {
                tenantId,
                layerId,
                stage,
                locationId,
                includeChildren = false,
                facility,
            } = args;

            if (!tenantId || !layerId) {
                throw new Error(
                    "Cannot add layer allocation without tenantId and layerId"
                );
            }

            const url = `${tenantApiPath(tenantId, "layers/")}${layerId}/locations`;

            const response = await callAvosApi(`/${url}`, { method: "POST",
                data: {
                    layer_id: layerId,
                    stage,
                    location_id: locationId,
                    include_children: includeChildren,
                    facility,
                } });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Location IDs are globally unique, so room allocations can target
            // only that room. Stage-only allocations have no room to target.
            queryClient.invalidateQueries({
                queryKey: variables.locationId
                    ? ["allocatedLayers", variables.locationId]
                    : ["allocatedLayers"],
            });
            queryClient.invalidateQueries({
                queryKey: ["layerLocationHistory", variables.tenantId, variables.layerId],
            });
        },
        onError: (error) => {
            console.error("Failed to add layer allocation:", error);
        },
    });
}

export function useDeleteLayerLocationHistoryMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { tenantId: string; layerId: string; history: any }) => {
            const { tenantId, layerId, history } = args;

            if (!tenantId || !layerId || !history?.id) {
                throw new Error("Cannot delete layer location history without tenantId, layerId, and id");
            }

            const url = `${tenantApiPath(tenantId, "layers/")}${layerId}/locations/${history.id}`;
            const response = await callAvosApi(`/${url}`, { method: "DELETE", data: history });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["layerLocationHistory", variables.tenantId, variables.layerId] });
            queryClient.invalidateQueries({ queryKey: ["layer", variables.tenantId, variables.layerId] });
        },
        onError: (error) => {
            console.error("Failed to delete layer location history:", error);
        },
    });
}

/**
 * Mutation hook for removing a child layer relationship.
 *
 * Usage:
 *   const mutation = useRemoveLayerChildMutation();
 *   mutation.mutate({layer, childLayerId});
 */
export function useRemoveLayerChildMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { layer: any; childLayerId: string }) => {
            const { layer, childLayerId } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error(
                    "Cannot remove child from layer without tenant_id and id"
                );
            }

            const url = `${tenantApiPath(layer.tenant_id, "layers/")}${layer.id}/children/${childLayerId}`;

            const response = await callAvosApi(`/${url}`, { method: "DELETE" });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate layer hierarchy
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.layer.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to remove layer child:", error);
        },
    });
}

/**
 * Mutation hook for creating and adding a child layer in one operation.
 *
 * Usage:
 *   const mutation = useCreateAndAddLayerChildMutation();
 *   mutation.mutate({layer, label, type});
 */
export function useCreateAndAddLayerChildMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: {
      layer: any;
      label: string;
      type: string;
    }) => {
            const { layer, label, type } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error(
                    "Cannot create child for layer without tenant_id and id"
                );
            }

            const url = `${tenantApiPath(layer.tenant_id, "layers/")}${layer.id}/children?create_child=true`;

            const response = await callAvosApi(`/${url}`, { method: "POST",
                data: {
                    parent_layer_id: layer.id,
                    layer_label: label,
                    layer_type: type,
                } });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate layer hierarchy
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.layer.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to create and add layer child:", error);
        },
    });
}

/**
 * Mutation hook for creating a sub-layer (e.g. pallet under a purchase order).
 *
 * Usage:
 *   const mutation = useCreateSubLayerMutation();
 *   mutation.mutate({tenantId, data: {...}});
 */
export function useCreateSubLayerMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { tenantId: string; data: any }) => {
            const { tenantId, data } = args;

            if (!tenantId) {
                throw new Error("Cannot create sub-layer without tenantId");
            }

            const url = tenantApiPath(tenantId, "layers/");
            const response = await callAvosApi(`/${url}`, { method: "POST", data: format2Backend(data) });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["layers", variables.tenantId] });
        },
        onError: (error) => {
            console.error("Failed to create sub-layer:", error);
        },
    });
}

/**
 * Mutation hook for adding an image to a layer.
 *
 * Usage:
 *   const mutation = useAddLayerImageMutation();
 *   mutation.mutate({layer, data: {type, base64, focusMode}});
 */
export function useAddLayerImageMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { layer: any; data: any }) => {
            const { layer, data } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error("Cannot add a layer image without tenant_id and id");
            }

            const url = tenantApiPath(layer.tenant_id, `images/layer/${layer.id}/add_image/`);
            const response = await callAvosApi(`/${url}`, { method: "POST", data });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["layers", variables.layer.tenant_id] });
        },
        onError: (error) => {
            console.error("Failed to add layer image:", error);
        },
    });
}

/**
 * Mutation hook for deleting an image from a layer.
 *
 * Usage:
 *   const mutation = useDeleteLayerImageMutation();
 *   mutation.mutate({layer, image});
 */
export function useDeleteLayerImageMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { layer: any; image: any }) => {
            const { layer, image } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error("Cannot delete a layer image without tenant_id and id");
            }

            const url = tenantApiPath(layer.tenant_id, `images/layer/${layer.id}/delete_image/`);
            const response = await callAvosApi(`/${url}`, { method: "DELETE", data: image });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["layers", variables.layer.tenant_id] });
        },
        onError: (error) => {
            console.error("Failed to delete layer image:", error);
        },
    });
}

/**
 * Mutation hook for uploading a generated report attached to a layer.
 * Not tenant-scoped in the URL; the backend derives the tenant from auth.
 *
 * Usage:
 *   const mutation = useAddReportMutation();
 *   mutation.mutate({configValue, file, layerId, folder});
 */
export function useAddReportMutation() {
    return useMutation({
        mutationFn: async (args: { configValue: string; file: any; layerId: string; folder: string }) => {
            const { configValue, file, layerId, folder } = args;
            const response = await callAvosApi("/report/", {
                method: "POST",
                data: {
                    config_value: configValue,
                    file,
                    entity: { id: layerId, type: "layer" },
                    folder,
                },
            });
            return response.data;
        },
        onError: (error) => {
            console.error("Failed to add report:", error);
        },
    });
}

/**
 * Mutation hook for uploading a document attached to a layer.
 * Not tenant-scoped in the URL; the backend derives the tenant from auth.
 *
 * Usage:
 *   const mutation = useAddDocumentMutation();
 *   mutation.mutate({configValue, file, layerId, folder});
 */
export function useAddDocumentMutation() {
    return useMutation({
        mutationFn: async (args: { configValue: string; file: any; layerId: string; folder: string }) => {
            const { configValue, file, layerId, folder } = args;
            const response = await callAvosApi("/report/document", {
                method: "POST",
                data: {
                    config_value: configValue,
                    file,
                    entity: { id: layerId, type: "layer" },
                    folder,
                },
            });
            return response.data;
        },
        onError: (error) => {
            console.error("Failed to add document:", error);
        },
    });
}

/**
 * Mutation hook for deleting a document attached to a layer.
 * Not tenant-scoped in the URL; the backend derives the tenant from auth.
 *
 * Usage:
 *   const mutation = useDeleteDocumentMutation();
 *   mutation.mutate({metaKey, blobName, layerId});
 */
export function useDeleteDocumentMutation() {
    return useMutation({
        mutationFn: async (args: { metaKey: string; blobName: string; layerId: string }) => {
            const { metaKey, blobName, layerId } = args;
            const response = await callAvosApi("/report/document", {
                method: "DELETE",
                data: {
                    meta_key: metaKey,
                    blob_name: blobName,
                    entity: { id: layerId, type: "layer" },
                },
            });
            return response.data;
        },
        onError: (error) => {
            console.error("Failed to delete document:", error);
        },
    });
}
